import { defineStore } from "pinia";
import {
  getTelephonyConfig,
  initiateCall,
  listCalls,
  type CallerIdMode,
  type InitiateCallPayload,
  type TelephonyConfig,
} from "@/services/telephonyApi";
import { useSnackbarStore } from "@/stores/snackbarStore";
import { gt } from "@/i18n/gt";

// Terminal Twilio call statuses that should make the ActiveCallBar vanish.
const TERMINAL_STATUSES = new Set([
  "completed",
  "failed",
  "busy",
  "no-answer",
  "canceled",
]);
// Hard safety timeout: even if we never see a terminal status (e.g. webhook
// lost), stop showing the bar after this many ms.
const MAX_CALL_AGE_MS = 30 * 60 * 1000; // 30 min
// Kundenfeedback Kanzlei Polz 27.10.2026 (I.4): „Es klingelt …“ soll zeitnah
// erscheinen – vorher 5 s, jetzt 2 s. Abgefragt werden nur die letzten
// Anrufe (ACTIVE_POLL_LIMIT), der aktive ist immer der jüngste.
const POLL_INTERVAL_MS = 2000;
const ACTIVE_POLL_LIMIT = 5;
// Endstatus („Besetzt“, „Nicht erreicht“, „Beendet“) so lange stehen lassen,
// dass man ihn lesen kann, bevor die Anrufleiste verschwindet.
const FINAL_STATUS_VISIBLE_MS = 5000;

// Rückmeldung Kanzlei Polz, 01.10.2026: Nach einem Anruf über KARS öffnet
// sich die Abfrage, ob die Gesprächsnotiz weitergeleitet werden soll – für
// tatsächlich gewählte Anrufe (verbunden, besetzt, nicht angenommen), nicht
// für technisch gescheiterte oder abgebrochene.
const NOTE_PROMPT_STATUSES = new Set(["completed", "busy", "no-answer"]);

/** Beendeter Anruf für die Abfrage – im Format der Telefonliste. */
export interface NotePromptCall {
  id: number;
  source: "portal";
  idCase: number | null;
  direction: "outbound" | "inbound";
  contactName: string | null;
  contactPhone: string | null;
  calledAt: string;
  status: string;
  summary: string | null;
  aiSummary: string | null;
}

export interface ActiveCall {
  callLogId: number;
  twilioCallSid: string;
  contactLabel: string;
  phoneTo: string;
  status: string;
  /** Twilio-Status des angerufenen Kontakts (Callback-Modus, zweites Bein). */
  calleeStatus?: string | null;
  startedAt: number; // epoch ms
  /** Zeitpunkt, ab dem das Gespräch steht (für „Verbunden 00:12“). */
  connectedAt?: number | null;
  mode: string;
  /** Ausblenden nach Endstatus ist bereits geplant. */
  endScheduled?: boolean;
  aiStatus?: string;
  declineNotified?: boolean;
  idCase?: number | null;
}

export interface CallStoreState {
  config: TelephonyConfig | null;
  configLoaded: boolean;
  activeCall: ActiveCall | null;
  dialing: boolean;
  /**
   * When non-null, a global confirmation dialog (mounted in App.vue) is
   * shown so the user can opt in to recording / AI transcription before
   * the call is actually initiated. The payload already contains the
   * resolved phone number / contact label / case context.
   */
  pendingCall: InitiateCallPayload | null;
  /** Beendeter Anruf, zu dem die Notiz-Abfrage offen ist (CallNotePrompt). */
  notePromptCall: NotePromptCall | null;
  /** Zählt gespeicherte Gesprächsnotizen hoch, damit Telefonlisten neu laden. */
  callNotesVersion: number;
  /** Anruf, zu dem die Abfrage schon geöffnet wurde – nie ein zweites Mal. */
  _notePromptedFor: number | null;
  _pollTimer: number | null;
}

/**
 * Global Pinia store for telephony. Keeps the currently active call and
 * exposes a single `startCall(payload)` action that the phone buttons
 * across the app can call.
 *
 * Current behaviour (callback-mode only):
 *   - POST /protected/telephony/calls asks the backend to ring the user's
 *     own phone via Twilio.
 *   - The backend returns a call log id + Twilio SID; we surface progress
 *     via the ActiveCallBar component. Final status is updated via the
 *     server-side status webhook, not via this store — the UI clears the
 *     active call when the user dismisses the bar or the page is reloaded.
 */
export const useCallStore = defineStore("call", {
  state: (): CallStoreState => ({
    config: null,
    configLoaded: false,
    activeCall: null,
    dialing: false,
    pendingCall: null,
    notePromptCall: null,
    callNotesVersion: 0,
    _notePromptedFor: null,
    _pollTimer: null,
  }),

  getters: {
    telephonyEnabled: (state) => !!state.config && state.config.enabled,
    hasAnyPhoneNumber: (state) =>
      !!state.config && (!!state.config.userMobile || !!state.config.userLandline),
  },

  actions: {
    async ensureConfig(force = false): Promise<TelephonyConfig | null> {
      if (this.configLoaded && !force) return this.config;
      try {
        this.config = await getTelephonyConfig();
      } catch (e) {
        this.config = null;
      } finally {
        this.configLoaded = true;
      }
      return this.config;
    },

    async startCall(payload: InitiateCallPayload) {
      const snackbar = useSnackbarStore();
      if (!payload.to || !payload.to.trim()) {
        snackbar.showError("Keine Telefonnummer hinterlegt.");
        return;
      }
      await this.ensureConfig();
      if (!this.telephonyEnabled) {
        snackbar.showError("Die Telefonie-Funktion ist nicht konfiguriert.");
        return;
      }
      if (!this.hasAnyPhoneNumber) {
        snackbar.showError(
          "Für den Rückruf-Anruf muss in Ihrem Benutzerprofil eine Mobil- oder Festnetznummer hinterlegt sein. Bitte tragen Sie eine Rufnummer unter Einstellungen → Profil ein."
        );
        return;
      }

      // Default to callback mode through the user's mobile if no device is
      // specified. The backend picks the first available number otherwise.
      const body: InitiateCallPayload = {
        device: payload.device ?? (this.config?.userMobile ? "mobile" : "landline"),
        ...payload,
      };

      this.dialing = true;
      try {
        const res = await initiateCall(body);
        this.activeCall = {
          callLogId: res.callLogId,
          twilioCallSid: res.twilioCallSid,
          contactLabel: payload.contactLabel || payload.to,
          phoneTo: payload.to,
          status: res.status || "queued",
          startedAt: Date.now(),
          mode: res.mode,
          idCase: payload.idCase ?? null,
        };
        snackbar.showSuccess(
          res.mode === "callback"
            ? gt("Anruf wird aufgebaut – Ihr Telefon klingelt gleich. Bitte abheben, dann wird gewählt.")
            : gt("Anruf wird aufgebaut.")
        );
        this._startPolling();
      } catch (e: any) {
        const msg =
          (e && (e.message || e.error || e.toString())) ||
          "Anruf konnte nicht initiiert werden.";
        snackbar.showError(msg);
      } finally {
        this.dialing = false;
      }
    },

    endCall() {
      this.activeCall = null;
      this._stopPolling();
    },

    /**
     * Open the global recording-/AI-consent dialog for the given call.
     * The actual Twilio request is issued by `confirmPendingCall(aiEnabled)`
     * once the user clicked "Anrufen". Falls Telefonie deaktiviert ist,
     * fällt diese Methode auf einen `tel:`-Link zurück.
     */
    async requestCall(payload: InitiateCallPayload) {
      const snackbar = useSnackbarStore();
      const num = String(payload.to || "").trim();
      if (!num) {
        snackbar.showError("Keine Telefonnummer hinterlegt.");
        return;
      }
      await this.ensureConfig();
      if (!this.telephonyEnabled) {
        // Fallback so the user can still place the call manually.
        try {
          window.open(`tel:${num.replace(/\s+/g, "")}`);
        } catch {
          snackbar.showError("Die Telefonie-Funktion ist nicht konfiguriert.");
        }
        return;
      }
      this.pendingCall = { ...payload, to: num };
    },

    /** Schließt die Notiz-Abfrage; saved meldet den Listen neue Daten. */
    dismissNotePrompt(saved = false) {
      this.notePromptCall = null;
      if (saved) this.callNotesVersion += 1;
    },

    cancelPendingCall() {
      this.pendingCall = null;
    },

    async confirmPendingCall(aiEnabled: boolean, callerIdMode?: CallerIdMode) {
      const payload = this.pendingCall;
      this.pendingCall = null;
      if (!payload) return;
      await this.startCall({ ...payload, aiEnabled, callerIdMode });
    },

    _startPolling() {
      this._stopPolling();
      this._pollTimer = window.setInterval(() => {
        this._pollStatus().catch(() => {
          /* swallow — next tick will retry */
        });
      }, POLL_INTERVAL_MS);
    },

    _stopPolling() {
      if (this._pollTimer !== null) {
        window.clearInterval(this._pollTimer);
        this._pollTimer = null;
      }
    },

    async _pollStatus() {
      const active = this.activeCall;
      if (!active) {
        this._stopPolling();
        return;
      }
      // Safety cutoff in case we never see a terminal status.
      if (Date.now() - active.startedAt > MAX_CALL_AGE_MS) {
        this.endCall();
        return;
      }
      const rows = await listCalls(ACTIVE_POLL_LIMIT);
      const row = rows.find((r) => r.id === active.callLogId);
      if (!row) return;
      if (row.status && row.status !== active.status) {
        active.status = row.status;
      }
      if (row.calleeStatus && row.calleeStatus !== active.calleeStatus) {
        active.calleeStatus = row.calleeStatus;
      }
      // Gesprächsdauer ab dem Moment, in dem der Kontakt abhebt (Callback)
      // bzw. das einzige Bein verbunden ist (Browser/API).
      const connected =
        active.calleeStatus === "in-progress" ||
        (active.mode !== "callback" && active.status === "in-progress");
      if (connected && !active.connectedAt) {
        active.connectedAt = Date.now();
      }
      if (row.aiStatus && row.aiStatus !== active.aiStatus) {
        active.aiStatus = row.aiStatus;
        if (row.aiStatus === "declined" && !active.declineNotified) {
          active.declineNotified = true;
          useSnackbarStore().showError(
            "Der Gesprächspartner hat die Aufzeichnung abgelehnt. Bitte machen Sie sich manuelle Notizen."
          );
        }
      }
      if (row.status && TERMINAL_STATUSES.has(row.status)) {
        if (NOTE_PROMPT_STATUSES.has(row.status) && this._notePromptedFor !== active.callLogId) {
          this._notePromptedFor = active.callLogId;
          this.notePromptCall = {
            id: active.callLogId,
            source: "portal",
            idCase: row.idCase ?? active.idCase ?? null,
            direction: row.direction === "inbound" ? "inbound" : "outbound",
            contactName: active.contactLabel || null,
            contactPhone: active.phoneTo || null,
            calledAt: row.startedAt || new Date(active.startedAt).toISOString(),
            status: row.status,
            summary: null,
            aiSummary: row.aiSummary ?? null,
          };
        }
        // Endstatus kurz sichtbar lassen, dann ausblenden. Bis dahin weiter
        // abfragen: Das Ergebnis des Kontakt-Beins („Besetzt“) kann nach dem
        // Ende des eigenen Beins eintreffen.
        if (!active.endScheduled) {
          active.endScheduled = true;
          window.setTimeout(() => {
            if (this.activeCall && this.activeCall.callLogId === active.callLogId) {
              this.endCall();
            }
          }, FINAL_STATUS_VISIBLE_MS);
        }
      }
    },
  },
});
